/**
 * @fileoverview Syndicator Showdown — Quick Pick Auto-Fill Strategies
 * @module utils/quickPick
 *
 * Pure functions with no side effects. No React. No Firestore.
 * Generates a complete, floor-validated allocation map for every race on a
 * slate, in the exact shape EntryContext.applyGeneratedStable() expects:
 *
 *   { [raceId]: { totalSpentOnRace, runnersPicked, runners: { [runnerId]: { equityPercent, purchaseCost } } } }
 *
 * This is a whole-slate "replace everything" generator — distinct from the
 * per-race "Auto-Fill This Leg" buttons in RaceCard (stableGenerator.js),
 * which use different named modes (Balanced/Value/Lucky Dip). Quick Pick
 * strategies here are Random / Market Fancies / AI Value (or Budget
 * Roughies fallback), each with rules specified by product.
 *
 * ─── PRICING ──────────────────────────────────────────────────────────────
 * All costs are computed through @ss/shared's calculatePurchaseCost (the
 * tiered T1..T5 curve) via utils/equityPricing.js — never a linear
 * `equity * basePrice` shortcut — so stored purchaseCost values always
 * agree with what BuyModal/ReviewSlip recompute later.
 *
 * The per-runner price used is whichever is available, in this priority:
 *   1. baseSharePrice — computed live from locked odds via
 *      calculateFieldPrices(), the same value RunnerRow/BuyModal already
 *      charge players. Always prefer this so Quick Pick never disagrees
 *      with a manual purchase of the same runner.
 *   2. runner.sharePrice / runner.generatedSharePrice — persisted by the
 *      value-engine recalculation pipeline, used only if odds are missing.
 *   3. runner.pricePerPercent — legacy/alternate field, last resort.
 * Runners with no resolvable positive price are skipped entirely.
 */

import { calculateFieldPrices } from '@ss/shared';
import { MAX_FIELD_COVERAGE } from '@ss/shared/constants';
import { costForEquity, findEquityToMeetSpend, findMaxEquityWithinBudget } from './equityPricing';

export const QUICK_PICK_STRATEGIES = {
  RANDOM:         'random',
  MARKET_FANCIES: 'market_fancies',
  AI_VALUE:       'ai_value',
};

// Per-race "Auto-Fill This Leg" buttons (RaceCard) — named Balanced / Value /
// Lucky Dip per product naming, but driven by the SAME buildRaceContext +
// validateAndTopUp pipeline as whole-slate Quick Pick above, so a per-race
// fill can never disagree with the whole-slate one on pricing, floor, or
// field-cap rules. Value/Lucky Dip reuse the AI Value / Random generators
// directly; Balanced has its own rating-led (not just priciest-first) picker.
export const AUTO_FILL_STRATEGIES = {
  BALANCED:  'balanced',
  VALUE:     QUICK_PICK_STRATEGIES.AI_VALUE,
  LUCKY_DIP: QUICK_PICK_STRATEGIES.RANDOM,
};

// Internal 8-way valueLabel tiers treated as "good value" for the AI Value
// strategy. The product spec named this tier "STEAL" — this codebase's
// value_engine_v1 uses GREAT_VALUE for that same top tier, so we map to the
// real field name rather than a label that doesn't exist in the data.
const PRIME_VALUE_LABELS = new Set(['GREAT_VALUE', 'GOOD_BUY', 'SPECULATIVE_VALUE']);

// ─── Runner helpers ─────────────────────────────────────────────────────────

function isActiveRunner(r) {
  if (r.scratched) return false;
  const s = r.status?.toLowerCase?.();
  return !s?.includes('scratch');
}

function resolveRunnerPrice(runner, computedBasePrice) {
  const price = (typeof computedBasePrice === 'number' && computedBasePrice > 0)
    ? computedBasePrice
    : (runner.sharePrice ?? runner.generatedSharePrice ?? runner.pricePerPercent);
  return (typeof price === 'number' && Number.isFinite(price) && price > 0) ? price : null;
}

function shuffleInPlace(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function emptyRaceAlloc() {
  return { runners: {}, totalSpentOnRace: 0, runnersPicked: 0 };
}

function buildRaceAllocation(runners) {
  const totalSpentOnRace = Object.values(runners).reduce((sum, r) => sum + (r.purchaseCost || 0), 0);
  return { runners, totalSpentOnRace, runnersPicked: Object.keys(runners).length };
}

/**
 * True if ANY active runner across the slate carries usable value-engine
 * output. Drives the AI Value vs. Budget Roughies button label/behaviour.
 */
export function hasValueEngineData(races) {
  return (Array.isArray(races) ? races : []).some(race =>
    (Array.isArray(race.runners) ? race.runners : []).some(r =>
      isActiveRunner(r) && r.valueLabel != null && r.valueLabel !== 'UNKNOWN'
    )
  );
}

// ─── Per-race context ───────────────────────────────────────────────────────

function buildRaceContext(race, avgSpendPerRace, kMultiplier) {
  const allRunners = Array.isArray(race.runners) ? race.runners : [];
  const active      = allRunners.filter(isActiveRunner);

  const priceList = calculateFieldPrices(allRunners, avgSpendPerRace, kMultiplier);
  const priceMap  = {};
  (Array.isArray(priceList) ? priceList : []).forEach(p => { priceMap[p.id] = p.baseSharePrice; });

  const runners = active
    .map(r => ({ ...r, _price: resolveRunnerPrice(r, priceMap[r.id]) }))
    .filter(r => r._price != null);

  return {
    raceId:            race.id,
    raceNumber:         race.raceNumber,
    raceName:           race.raceName,
    activeRunnerCount:  active.length,
    maxRunners:         Math.max(1, Math.floor(active.length * MAX_FIELD_COVERAGE)),
    runners,
  };
}

// ─── Strategy: Random ───────────────────────────────────────────────────────

function generateRandomAllocation(ctx, avgSpendPerRace) {
  const { runners, maxRunners } = ctx;
  if (runners.length === 0) return emptyRaceAlloc();

  // Cap the number of runners the SAME way Balanced/Value do (max 4) — the
  // 50% field-coverage rule (maxRunners) is still respected as an upper
  // ceiling, but a big field (e.g. 18 runners -> maxRunners=9) must never
  // let "Lucky Dip" spray equity across half the field. Random should feel
  // like a handful of picks, not a mass buy.
  const selectionCap = Math.max(1, Math.min(4, maxRunners, runners.length));
  const numToSelect   = 1 + Math.floor(Math.random() * selectionCap);
  const selected      = shuffleInPlace([...runners]).slice(0, numToSelect);

  const runnersOut = {};
  selected.forEach(r => {
    const pct = Math.min(100, Math.round(5 + Math.random() * 25)); // 5–30%
    runnersOut[r.id] = { equityPercent: pct, purchaseCost: costForEquity(r._price, pct) };
  });

  let totalSpend = Object.values(runnersOut).reduce((s, r) => s + r.purchaseCost, 0);

  // Cap total race spend at ~avgSpendPerRace — scale targets down and
  // recompute cost through the real tiered curve (never linear-scale cost).
  if (totalSpend > avgSpendPerRace) {
    const scale = avgSpendPerRace / totalSpend;
    selected.forEach(r => {
      const scaledPct = Math.max(1, Math.floor(runnersOut[r.id].equityPercent * scale));
      runnersOut[r.id] = { equityPercent: scaledPct, purchaseCost: costForEquity(r._price, scaledPct) };
    });
  }

  return buildRaceAllocation(runnersOut);
}

// ─── Strategy: Market Fancies ───────────────────────────────────────────────

function generateMarketFanciesAllocation(ctx, avgSpendPerRace) {
  const { runners, maxRunners } = ctx;
  if (runners.length === 0) return emptyRaceAlloc();

  const sorted    = [...runners].sort((a, b) => b._price - a._price); // most expensive first
  const numToPick = Math.max(1, Math.min(3, maxRunners, sorted.length));
  const selected  = sorted.slice(0, numToPick);

  const weights    = [0.5, 0.3, 0.2].slice(0, selected.length);
  const weightSum  = weights.reduce((s, w) => s + w, 0);

  const runnersOut = {};
  let overflow = 0;
  selected.forEach((runner, i) => {
    const budget = (avgSpendPerRace * weights[i] / weightSum) + overflow;
    const pct    = findMaxEquityWithinBudget(runner._price, 0, budget, 100);
    const cost   = costForEquity(runner._price, pct);
    if (pct > 0) runnersOut[runner.id] = { equityPercent: pct, purchaseCost: cost };
    overflow = Math.max(0, budget - cost); // redistribute unspent budget to the next runner
  });

  return buildRaceAllocation(runnersOut);
}

// ─── Strategy: AI Value (falls back to Budget Roughies) ───────────────────

function generateAiValueAllocation(ctx, avgSpendPerRace) {
  const { runners, maxRunners } = ctx;
  if (runners.length === 0) return emptyRaceAlloc();

  const valueRunners = runners.filter(r => PRIME_VALUE_LABELS.has(r.valueLabel));
  const runnersOut    = {};

  if (valueRunners.length > 0) {
    const selected = [...valueRunners]
      .sort((a, b) => (b.valueIndex ?? 0) - (a.valueIndex ?? 0))
      .slice(0, Math.max(1, Math.min(4, maxRunners)));

    const perRunnerBudget = avgSpendPerRace / selected.length;
    let overflow = 0;
    selected.forEach(runner => {
      const budget = perRunnerBudget + overflow;
      const pct    = findMaxEquityWithinBudget(runner._price, 0, budget, 100);
      const cost   = costForEquity(runner._price, pct);
      if (pct > 0) runnersOut[runner.id] = { equityPercent: pct, purchaseCost: cost };
      overflow = Math.max(0, budget - cost);
    });
  } else {
    // Budget Roughies fallback — cheapest runners, buy each to 100% (or max
    // affordable) starting from the cheapest until the budget is exhausted.
    const selected = [...runners]
      .sort((a, b) => a._price - b._price)
      .slice(0, Math.max(1, Math.min(4, maxRunners)));

    let remaining = avgSpendPerRace;
    for (const runner of selected) {
      if (remaining <= 0) break;
      const pct = findMaxEquityWithinBudget(runner._price, 0, remaining, 100);
      if (pct <= 0) continue;
      const cost = costForEquity(runner._price, pct);
      runnersOut[runner.id] = { equityPercent: pct, purchaseCost: cost };
      remaining -= cost;
    }
  }

  return buildRaceAllocation(runnersOut);
}

// ─── Strategy: Balanced (per-race only) ─────────────────────────────────────
//
// "Choose 2-4 runners using a rating/probability mix, spread spend toward
// avgSpendPerRace, not only favourites, not only roughies." Rating-led
// (displayRating desc) rather than price-led like Market Fancies, so it
// doesn't just buy the shortest-priced runners.

function generateBalancedAllocation(ctx, avgSpendPerRace) {
  const { runners, maxRunners } = ctx;
  if (runners.length === 0) return emptyRaceAlloc();

  const numToSelect = Math.max(1, Math.min(4, maxRunners, runners.length));

  // Prefer runners with a real rating; if none of the field has been rated
  // yet, fall back to the full pool in its existing (odds-driven) order
  // rather than claiming a rating-based pick it can't actually make.
  const rated = runners.filter(r => r.displayRating != null);
  const pool  = rated.length >= numToSelect ? rated : runners;
  const selected = [...pool]
    .sort((a, b) => (b.displayRating ?? 0) - (a.displayRating ?? 0))
    .slice(0, numToSelect);

  const perRunnerBudget = avgSpendPerRace / selected.length;
  const runnersOut = {};
  let overflow = 0;
  selected.forEach(runner => {
    const budget = perRunnerBudget + overflow;
    const pct    = findMaxEquityWithinBudget(runner._price, 0, budget, 100);
    const cost   = costForEquity(runner._price, pct);
    if (pct > 0) runnersOut[runner.id] = { equityPercent: pct, purchaseCost: cost };
    overflow = Math.max(0, budget - cost);
  });

  return buildRaceAllocation(runnersOut);
}

// ─── Post-allocation validation / top-up pass (required for all strategies) ─

/**
 * Ensures every race meets its per-race floor. First tries topping up
 * existing holdings, then adding the cheapest unselected runner (if the
 * field cap allows), then aborts the ENTIRE quick pick if the floor still
 * cannot be met for any race.
 */
function validateAndTopUp(allocations, raceContexts, perRaceFloor) {
  const next = {};

  for (const ctx of raceContexts) {
    const raceId = ctx.raceId;
    const source = allocations[raceId] || emptyRaceAlloc();
    const alloc  = {
      runners:          { ...source.runners },
      totalSpentOnRace: source.totalSpentOnRace,
      runnersPicked:    source.runnersPicked,
    };

    if (alloc.totalSpentOnRace < perRaceFloor) {
      // Pass 1 — top up existing holdings toward 100%, cheapest cost-per-point first.
      for (const runner of ctx.runners) {
        if (alloc.totalSpentOnRace >= perRaceFloor) break;
        const holding = alloc.runners[runner.id];
        if (!holding || holding.equityPercent >= 100) continue;

        const needed = perRaceFloor - alloc.totalSpentOnRace;
        const newPct = findEquityToMeetSpend(runner._price, holding.equityPercent, needed, 100);
        if (newPct > holding.equityPercent) {
          const newCost = costForEquity(runner._price, newPct);
          alloc.totalSpentOnRace += newCost - holding.purchaseCost;
          alloc.runners[runner.id] = { equityPercent: newPct, purchaseCost: newCost };
        }
      }

      // Pass 2 — add the cheapest unselected runner, if field cap allows.
      if (alloc.totalSpentOnRace < perRaceFloor && Object.keys(alloc.runners).length < ctx.maxRunners) {
        const cheapest = ctx.runners
          .filter(r => !alloc.runners[r.id])
          .sort((a, b) => a._price - b._price)[0];

        if (cheapest) {
          const needed = perRaceFloor - alloc.totalSpentOnRace;
          const pct    = findEquityToMeetSpend(cheapest._price, 0, needed, 100);
          const cost   = costForEquity(cheapest._price, pct);
          alloc.runners[cheapest.id] = { equityPercent: pct, purchaseCost: cost };
          alloc.totalSpentOnRace += cost;
        }
      }

      alloc.runnersPicked = Object.keys(alloc.runners).length;

      // Pass 3 — still short: abort the whole quick pick.
      if (alloc.totalSpentOnRace < perRaceFloor) {
        return {
          allocations: null,
          aborted:     true,
          message:     `Quick Pick could not meet the race floor requirement for ${ctx.raceName || `Leg ${ctx.raceNumber}`}. Please select runners manually.`,
        };
      }
    }

    next[raceId] = alloc;
  }

  return { allocations: next, aborted: false, message: null };
}

// ─── Public API ───────────────────────────────────────────────────────────

/**
 * Generates a full-slate allocation map for the chosen Quick Pick strategy,
 * then validates and tops up every race against its per-race floor.
 *
 * @param {Object}  params
 * @param {string}  params.strategy             - One of QUICK_PICK_STRATEGIES
 * @param {Array}   params.races                 - Slate races (with runners[])
 * @param {number}  params.avgSpendPerRace
 * @param {number}  [params.kMultiplier]
 * @param {number}  params.perRaceFloor
 * @param {number}  [params.totalStartingCapital] - Wallet ceiling across the whole slate
 * @returns {{ allocations: Object|null, aborted: boolean, message: string|null }}
 */
export function runQuickPick({
  strategy,
  races,
  avgSpendPerRace,
  kMultiplier = 2.900,
  perRaceFloor,
  totalStartingCapital,
}) {
  const safeRaces = (Array.isArray(races) ? races : [])
    .filter(r => Array.isArray(r.runners) && r.runners.length > 0);

  if (safeRaces.length === 0) {
    return { allocations: null, aborted: true, message: 'No races are available to auto-fill.' };
  }

  const raceContexts = safeRaces.map(race => buildRaceContext(race, avgSpendPerRace, kMultiplier));

  const rawAllocations = {};
  raceContexts.forEach(ctx => {
    switch (strategy) {
      case QUICK_PICK_STRATEGIES.MARKET_FANCIES:
        rawAllocations[ctx.raceId] = generateMarketFanciesAllocation(ctx, avgSpendPerRace);
        break;
      case QUICK_PICK_STRATEGIES.AI_VALUE:
        rawAllocations[ctx.raceId] = generateAiValueAllocation(ctx, avgSpendPerRace);
        break;
      case QUICK_PICK_STRATEGIES.RANDOM:
      default:
        rawAllocations[ctx.raceId] = generateRandomAllocation(ctx, avgSpendPerRace);
    }
  });

  const validated = validateAndTopUp(rawAllocations, raceContexts, perRaceFloor);
  if (validated.aborted) return validated;

  const totalSpend = Object.values(validated.allocations)
    .reduce((sum, a) => sum + (a.totalSpentOnRace || 0), 0);

  if (totalStartingCapital != null && totalSpend > totalStartingCapital) {
    return {
      allocations: null,
      aborted:     true,
      message:     'Quick Pick could not fit within your total wallet balance. Please select runners manually.',
    };
  }

  return { allocations: validated.allocations, aborted: false, message: null, totalSpend };
}

/**
 * Per-race version of the same engine, used by RaceCard's "Auto-Fill This
 * Leg" buttons (Balanced / Value / Lucky Dip). Only ever touches ONE race's
 * allocation — the caller is responsible for merging the result into the
 * rest of the slate's allocations (and for confirming replacement with the
 * player if that race already has selections).
 *
 * Respects the same hard rules as whole-slate Quick Pick:
 *   - 100% max per runner (findEquityToMeetSpend/findMaxEquityWithinBudget cap)
 *   - max 50% field coverage (ctx.maxRunners, from MAX_FIELD_COVERAGE)
 *   - tops up toward the race floor where possible (validateAndTopUp)
 *   - aims toward avgSpendPerRace, never presumes 100% is affordable
 *   - never exceeds the wallet — checked against `budgetCeiling`, which the
 *     caller should pass as (remainingWallet + whatever this race currently
 *     has spent, since replacing frees that back up first)
 *
 * @param {Object}  params
 * @param {Object}  params.race
 * @param {number}  params.avgSpendPerRace
 * @param {number}  [params.kMultiplier]
 * @param {number}  params.perRaceFloor
 * @param {string}  params.strategy       - One of AUTO_FILL_STRATEGIES
 * @param {number}  [params.budgetCeiling] - Max this race is allowed to spend in total
 * @returns {{ allocation: Object|null, aborted: boolean, message: string|null }}
 */
export function runAutoFillForRace({
  race,
  avgSpendPerRace,
  kMultiplier = 2.900,
  perRaceFloor,
  strategy,
  budgetCeiling = null,
}) {
  if (!race || !Array.isArray(race.runners) || race.runners.length === 0) {
    return { allocation: null, aborted: true, message: 'No runners available to auto-fill this race.' };
  }

  const ctx = buildRaceContext(race, avgSpendPerRace, kMultiplier);
  if (ctx.runners.length === 0) {
    return { allocation: null, aborted: true, message: 'No priced runners available to auto-fill this race yet.' };
  }

  let raw;
  switch (strategy) {
    case AUTO_FILL_STRATEGIES.VALUE:
      raw = generateAiValueAllocation(ctx, avgSpendPerRace);
      break;
    case AUTO_FILL_STRATEGIES.LUCKY_DIP:
      raw = generateRandomAllocation(ctx, avgSpendPerRace);
      break;
    case AUTO_FILL_STRATEGIES.BALANCED:
    default:
      raw = generateBalancedAllocation(ctx, avgSpendPerRace);
  }

  const validated = validateAndTopUp({ [ctx.raceId]: raw }, [ctx], perRaceFloor);
  if (validated.aborted) {
    return { allocation: null, aborted: true, message: validated.message };
  }

  const allocation = validated.allocations[ctx.raceId];

  if (budgetCeiling != null && allocation.totalSpentOnRace > budgetCeiling) {
    return {
      allocation: null,
      aborted:    true,
      message:    `Auto-fill for ${ctx.raceName || `Leg ${ctx.raceNumber}`} needs `
        + `${formatMoney(allocation.totalSpentOnRace)} but only ${formatMoney(budgetCeiling)} `
        + `is available in your wallet for this race. Please select runners manually.`,
    };
  }

  return { allocation, aborted: false, message: null };
}

function formatMoney(v) {
  return '$' + Math.round(v || 0).toLocaleString('en-AU');
}
